import { useEffect, useRef, useState, type JSX } from 'react';
import type { EnginePostureView } from '@term-deck/core/engine-posture.js';
import { api } from '../api/client';
import { EnginePosturePanel } from './EnginePosturePanel';
import { IconPause } from './icons';

/**
 * "Pausar tudo" ao alcance de qualquer tela.
 *
 * A pausa global (M436) ja existia e ja parava MASTER, motores, comunicacao, operacoes por projeto e rotinas
 * de ociosidade — mas o unico controle vivia dentro do `/chat` do MASTER. Em 10/09/2026 o dono perguntou se
 * "daria para implementar uma chave global que pausa tudo" sem saber que ela existia: falha de DESCOBERTA,
 * nao de capacidade. O controle sobe para a navegacao principal e reusa o MESMO painel (mesma regra
 * human-only, mesmo motivo obrigatorio, mesmos tres estados), sem uma segunda fonte de verdade.
 */

/** Poll longo de proposito: a postura muda por decisao humana rara. */
const POLL_MS = 60_000;

export function useGlobalAutonomyPosture(): [
  EnginePostureView | null,
  (view: EnginePostureView) => void,
] {
  const [view, setView] = useState<EnginePostureView | null>(null);
  useEffect(() => {
    let alive = true;
    const load = (): void => {
      api
        .getEnginePosture()
        .then((next) => {
          if (alive) setView(next);
        })
        .catch(() => undefined);
    };
    load();
    const timer = setInterval(load, POLL_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);
  return [view, setView];
}

export function GlobalPauseControl({
  view,
  onChange,
}: {
  view: EnginePostureView | null;
  onChange: (view: EnginePostureView) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const host = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent): void => {
      if (!host.current?.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setOpen(false);
        host.current?.querySelector<HTMLButtonElement>('.global-pause-trigger')?.focus();
      }
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);
  // Ilegivel e tratado como pausa pelo servidor (fail-closed); a navegacao diz o mesmo.
  const paused = view?.ativa === true;
  return (
    <div className="global-pause" ref={host}>
      <button
        type="button"
        className={`global-pause-trigger${paused ? ' is-paused' : ''}`}
        data-testid="global-pause-trigger"
        aria-expanded={open}
        aria-controls="global-pause-panel"
        onClick={() => setOpen(!open)}
        title={
          paused
            ? 'Autonomia em repouso: MASTER, motores, operações e rotinas parados. Clique para ver ou retomar.'
            : 'Pausar MASTER, motores, operações, rotinas de ociosidade e comunicação automática'
        }
      >
        <IconPause />
        <span>{paused ? 'Tudo pausado' : 'Pausar tudo'}</span>
      </button>
      {open && (
        <div className="global-pause-panel" id="global-pause-panel">
          <EnginePosturePanel onStateChange={onChange} />
        </div>
      )}
    </div>
  );
}
